import { useFeatureFlagEnabled } from "@posthog/react";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useMemo } from "react";
import { useFindMentorship } from "@/lib/api/mentorships";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import { applyD7TriggerQaToAttentionSignals } from "../d7-trigger-qa";
import {
  buildAttentionBannerSignals,
  resolveAttentionBanner,
  type AttentionBannerDescriptor,
} from "./attention-banner";

export interface UseResolvedBannerParams {
  menteeId: string | null;
  enabled: boolean;
}

export interface UseResolvedBannerResult {
  banner: AttentionBannerDescriptor | null;
  mentorshipId: string | null;
}

export function useResolvedBanner({
  menteeId,
  enabled,
}: UseResolvedBannerParams): UseResolvedBannerResult {
  const store = useSendbirdStateContext();
  const mentorId = store.config.userId;

  const { data: mentorship } = useFindMentorship(mentorId, menteeId ?? "");
  const day1Enabled = useFeatureFlagEnabled("enable-day1-banner");
  const day2Enabled = useFeatureFlagEnabled("enable-day2-magic-moment");
  const rawQaScenario = useDevOverridesStore(
    (state) => state.d7TriggerQaScenario,
  );
  const qaScenario = devOverridesEnabled ? rawQaScenario : null;

  const banner = useMemo(() => {
    if (!enabled) return null;
    const signals = applyD7TriggerQaToAttentionSignals(
      buildAttentionBannerSignals(mentorship, {
        day1Enabled: Boolean(day1Enabled),
        day2Enabled: Boolean(day2Enabled),
      }),
      qaScenario,
    );
    return resolveAttentionBanner(signals);
  }, [enabled, mentorship, day1Enabled, day2Enabled, qaScenario]);

  return { banner, mentorshipId: mentorship?.id ?? null };
}
